import { router } from '@inertiajs/react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  type Feedback26CoStep,
  useFeedback26CoStore,
} from '@/stores/feedback-26-co';
import { useFeedbackEdition } from './edition-context';

interface NavigationButtonsProps {
  currentStep: Feedback26CoStep;
  publicId: string;
  hasMentor: boolean;
}

export function NavigationButtons({
  currentStep,
  publicId,
  hasMentor,
}: NavigationButtonsProps) {
  const {
    previousStep,
    nextStep,
    canGoBack,
    formData,
    setIsLoading,
    setError,
    setIsSubmitted,
    clearStorage,
  } = useFeedback26CoStore();
  const { editionSlug, asksHackRacer } = useFeedbackEdition();
  const [isLoading, setLocalLoading] = useState(false);
  const hackRacerAnswered =
    !asksHackRacer ||
    (formData.hackRacerRating !== undefined &&
      formData.hackRacerRating >= 1 &&
      formData.hackRacerRating <= 5);
  const [error, setLocalError] = useState<string | null>(null);

  const isStepComplete = (): boolean => {
    switch (currentStep) {
      case 'overall':
        return (
          formData.overallRating !== undefined &&
          formData.overallRating >= 1 &&
          formData.npsScore !== undefined &&
          formData.npsScore >= 0 &&
          formData.participationIntent !== undefined
        );

      case 'quality': {
        const ratings = formData.eventQualityRatings;
        return (
          Object.values(ratings).every((v) => v >= 1 && v <= 5) &&
          hackRacerAnswered
        );
      }

      case 'improvement':
        return !!formData.bestPart && formData.bestPart.length > 0;

      case 'sponsors':
        return (
          !!formData.sponsorUnaidedRecall &&
          formData.sponsorUnaidedRecall.length > 0
        );

      case 'future':
        return formData.startupIntent !== undefined;

      case 'extras':
        return formData.feedbackUsagePermission !== undefined;

      case 'aura':
        return (
          formData.auraRating !== undefined &&
          formData.auraRating >= 1 &&
          formData.auraRating <= 10
        );

      case 'thank-you':
        return true;

      default:
        return true;
    }
  };

  const handleBack = () => {
    if (canGoBack()) {
      previousStep();
    }
  };

  const handleContinue = () => {
    setLocalError(null);

    if (!isStepComplete()) {
      setLocalError('Por favor completa todos los campos requeridos');
      return;
    }

    if (currentStep !== 'aura') {
      nextStep();
      return;
    }

    if (
      formData.overallRating === undefined ||
      formData.npsScore === undefined ||
      !formData.participationIntent ||
      !formData.bestPart ||
      !formData.sponsorUnaidedRecall ||
      !formData.startupIntent ||
      !formData.feedbackUsagePermission ||
      formData.auraRating === undefined ||
      !hackRacerAnswered
    ) {
      setLocalError('Por favor completa todos los campos requeridos');
      return;
    }

    setLocalLoading(true);
    setIsLoading(true);

    router.post(
      `/hacker/${publicId}/hack-${editionSlug}/feedback`,
      {
        overallRating: formData.overallRating,
        npsScore: formData.npsScore,
        participationIntent: formData.participationIntent,
        eventQualityRatings: formData.eventQualityRatings,
        bestPart: formData.bestPart,
        worstPart: formData.worstPart,
        suggestions: formData.suggestions,
        sponsorUnaidedRecall: formData.sponsorUnaidedRecall,
        sponsorsInteracted: formData.sponsorsInteracted ?? [],
        sponsorWorkIntent: formData.sponsorWorkIntent ?? '',
        sponsorComments: formData.sponsorComments,
        startupIntent: formData.startupIntent,
        fundingPreference: formData.fundingPreference ?? '',
        startupAmbition: formData.startupAmbition ?? '',
        howHeardAbout: formData.howHeardAbout,
        additionalComments: formData.additionalComments,
        mediaUrls: formData.mediaUrls ?? [],
        feedbackUsagePermission: formData.feedbackUsagePermission,
        mentorRating: hasMentor ? formData.mentorRating : undefined,
        auraRating: formData.auraRating,
        hackRacerRating: asksHackRacer ? formData.hackRacerRating : undefined,
      },
      {
        onSuccess: () => {
          clearStorage();
          setIsSubmitted(true);
          nextStep();
        },
        onError: (errors) => {
          const message =
            (errors as unknown as Record<string, string | undefined>).base ||
            'Error al enviar el feedback';
          setLocalError(message);
          setError(message);
        },
        onFinish: () => {
          setLocalLoading(false);
          setIsLoading(false);
        },
      },
    );
  };

  const isFinalStep = currentStep === 'aura';
  const isThankYouStep = currentStep === 'thank-you';

  if (isThankYouStep) {
    return null;
  }

  return (
    <div className="space-y-3 border-border border-t pt-8">
      {error && (
        <div className="rounded border border-destructive bg-destructive/10 p-3 text-destructive text-sm">
          {error}
        </div>
      )}

      <div className="flex items-center gap-3">
        {canGoBack() && (
          <Button variant="outline" onClick={handleBack} disabled={isLoading}>
            ← Atras
          </Button>
        )}

        <div className="flex-1" />

        <Button
          onClick={handleContinue}
          disabled={isLoading || !isStepComplete()}
          className="bg-primary text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-muted"
        >
          {isLoading ? (
            <>
              <span className="mr-2 animate-spin">⏳</span>
              {isFinalStep ? 'Enviando...' : 'Cargando...'}
            </>
          ) : isFinalStep ? (
            'Enviar feedback'
          ) : (
            'Continuar →'
          )}
        </Button>
      </div>
    </div>
  );
}
